/**
 * The workspace, as a query, plus the one mutation B0 performs.
 *
 * The workspace is server state and gets its own key prefix rather than hanging off the
 * session's: `SESSION_QUERY_KEY` is cleared on sign-out and on a failed `GET /auth/me`, and a
 * workspace cached under it would be dropped for reasons that have nothing to do with the
 * workspace. Claiming invalidates both, because it changes an answer each of them holds —
 * `claimed_at` here, `home_workspace_claimed` there.
 */

import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { SESSION_QUERY_KEY, useActiveWorkspaceId } from '@/features/session';

import type { Workspace, WorkspaceClaim } from './api';
import { claimWorkspace, readNormCatalogue, readWorkspaceRepresentation } from './api';

/**
 * Everything derived from the caller's company hangs off this prefix, and **every key under
 * it names which company** (`KLQ-20260819-007` §6).
 *
 * The id is not decoration. A person may work for two studios, and `GET /workspace` answers
 * whichever one the request acted as — so a key without it would serve studio A's name,
 * domains and people under studio B's heading for as long as the entry stayed fresh. That is
 * one stale render of the wrong company's data, which is the failure this spec is most likely
 * to produce and the hardest to notice.
 *
 * The bare prefix stays useful for invalidating everything at once, which is what a claim and
 * a sign-out both want.
 */
export const WORKSPACE_QUERY_KEY = ['workspace'] as const;

export function workspaceQueryKey(workspaceId: string) {
  return [...WORKSPACE_QUERY_KEY, workspaceId] as const;
}

/**
 * The company **and the validator the response carried**, in one cache entry.
 *
 * `KLQ-20260819-008` §1: an ETag describes the representation the client is holding, so
 * holding it anywhere else — a ref, a second key — drifts the moment a refetch lands, and a
 * stale validator is a `412` the person did nothing to earn. Every reader that only wants
 * the company selects `.data` out of it; only B3 ever looks at `.etag`.
 */
export function workspaceQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: workspaceQueryKey(workspaceId),
    queryFn: readWorkspaceRepresentation,
    // A company's name and country change roughly never. Nothing in Kalqa is realtime
    // (CLAUDE.md, data-fetching rules), and B0 reads this once on mount to prefill.
    staleTime: 5 * 60_000,
  });
}

/**
 * The company itself, for the screens that only read it.
 *
 * The workspace it reads is the one this tab is acting as, and the request carries the same
 * id in its header — so the key and the answer cannot describe different companies.
 */
export function useWorkspace() {
  const workspaceId = useActiveWorkspaceId();
  return useQuery({
    ...workspaceQueryOptions(workspaceId ?? ''),
    enabled: workspaceId !== null,
    select: (held) => held.data,
  });
}

/**
 * Claim, then forget every cached answer the claim just changed.
 *
 * `invalidateQueries` rather than `setQueryData` with the response: the claim's effect is
 * wider than the row it returns — `home_workspace_claimed` on `GET /auth/me` flips too, and
 * that boolean is what decides whether the caller is sent back here on the next navigation.
 * Writing one and leaving the other stale is how a claimed workspace bounces to `/setup`.
 */
export function useClaimWorkspace() {
  return useMutation<Workspace, Error, WorkspaceClaim>({
    mutationFn: claimWorkspace,
    // Not `retry: false` for its own sake — the client's own 401 rotation has already run by
    // the time an error reaches here, and re-POSTing a claim would only earn a 409.
    retry: false,
  });
}

/**
 * The invalidation both success paths share — a real claim and a `409 already claimed`.
 *
 * **It is awaited, and that is the whole point.** `invalidateQueries` marks the entries stale
 * and starts a refetch, but the cache keeps serving the *previous* answer until that refetch
 * lands. A caller that navigated home in the same tick would therefore be routed by a `me`
 * that still says `home_workspace_claimed: false` — and `HomeRoute` answers that with a
 * redirect straight back into B0, so the person sets their company up twice. Returning the
 * settled promise is what makes "forget, then leave" mean what it reads like.
 */
export function useForgetWorkspaceState(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: WORKSPACE_QUERY_KEY }),
    ]);
  };
}

/**
 * The norm catalogue — **fetched once per session and shared by everything that renders a
 * norm** (`KLQ-20260827-001`; the ecosystem data-fetching rule in `~/.claude/CLAUDE.md`).
 *
 * WHY THE KEY DOES NOT NAME A WORKSPACE, when every other key in this file does. The rule
 * `KLQ-20260819-007` §6 states is about *tenant* data: `GET /workspace` answers a different
 * company per header, so a key without the id would serve one firm's name under another's
 * heading. `GET /api/v1/norms` is the opposite — it is seeded from a migration, it carries no
 * `workspace_id` column at all, and it answers byte-identical to every caller in every
 * tenant. Naming the workspace here would mean re-fetching an unchanged list every time a
 * person switches studios, which is the cost the rule exists to avoid rather than a case of it.
 *
 * WHY IT NEVER REFETCHES. `staleTime: Infinity` is the whole mechanism: a query that is never
 * stale is never refetched on a mount, a focus or a reconnect, so walking B0 → Studio settings
 * → back spends exactly one request. The three `refetchOn…: false` lines below are therefore
 * redundant *today* and are written anyway, because they say the intent out loud where a later
 * reader is looking — a `staleTime` shortened for some unrelated reason would otherwise turn a
 * reference list into a per-navigation fetch silently. `gcTime: Infinity` keeps the entry
 * through a moment where no screen is mounted (the wizard unmounts before settings mounts).
 *
 * The list changes when a law does. Nothing polls it, nothing subscribes to it, and a
 * deployment that seeds a new norm reaches an open tab on its next reload — which is the same
 * moment the code that renders the new norm arrives.
 */
export const NORM_CATALOGUE_QUERY_KEY = ['norms'] as const;

export function normCatalogueQueryOptions() {
  return queryOptions({
    queryKey: NORM_CATALOGUE_QUERY_KEY,
    queryFn: readNormCatalogue,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/** The catalogue, for the wizard and for Studio settings. One entry, one request, shared. */
export function useNormCatalogue() {
  return useQuery(normCatalogueQueryOptions());
}
